import React, { useState } from "react";
import { Transition } from "react-transition-group";
import { Highlighter, StickyNote, Trash2 } from "lucide-react";
import "./LessonNotes.css";
import { sortNotesByLessonOrder } from "./noteUtils";

const TABS = [
  { id: "all", label: "All" },
  { id: "highlights", label: "Highlights" },
  { id: "notes", label: "Notes" },
];

function panelStyleForState(state) {
  if (state === "entering") {
    return { animation: "moveNotesPanel .3s forwards" };
  }
  if (state === "entered") {
    return { transform: "translateX(0)" };
  }
  return { animation: "moveNotesPanel .3s reverse backwards" };
}

function overlayStyleForState(state) {
  if (state === "entering") {
    return { animation: "showNotesOverlay .3s forwards" };
  }
  if (state === "entered") {
    return { opacity: 1 };
  }
  return { animation: "showNotesOverlay .3s backwards reverse" };
}

function itemHasNote(item) {
  return !!(item?.note && String(item.note).trim());
}

function filterNotesByTab(notes, tab) {
  if (tab === "highlights") return notes.filter((item) => !itemHasNote(item));
  if (tab === "notes") return notes.filter(itemHasNote);
  return notes;
}

function groupNotesByLesson(notes) {
  const groups = [];
  const indexByLesson = new Map();
  notes.forEach((item) => {
    const key = String(item.sectionNumber || "");
    if (!indexByLesson.has(key)) {
      indexByLesson.set(key, groups.length);
      groups.push({
        sectionNumber: item.sectionNumber,
        sectionTitle: item.sectionTitle,
        items: [],
      });
    }
    groups[indexByLesson.get(key)].items.push(item);
  });
  return groups;
}

function emptyCopy(tab, hasAnyNotes) {
  if (!hasAnyNotes) {
    return "Highlight a section or add a note while reading";
  }
  if (tab === "highlights") return "No highlights yet";
  if (tab === "notes") return "No notes yet";
  return "Highlight a section or add a note while reading";
}

function previewText(item) {
  if (item.missing) return "This section is no longer available";
  if (!item.previewText) return "No text in this section";
  return `${item.previewText.slice(0, 280)}${
    item.previewText.length > 280 ? "…" : ""
  }`;
}

export default function LessonNotesPanel({
  isOpen,
  isDarkMode,
  notes,
  onClose,
  onJump,
  onDelete,
}) {
  const [tab, setTab] = useState("all");
  const orderedNotes = sortNotesByLessonOrder(notes);
  const filteredNotes = filterNotesByTab(orderedNotes, tab);
  const groups = groupNotesByLesson(filteredNotes);

  return (
    <Transition in={isOpen} timeout={300} mountOnEnter unmountOnExit>
      {(state) => (
        <>
          <div
            className="lesson-notes-overlay"
            style={overlayStyleForState(state)}
            onClick={onClose}
          />
          <aside
            className={`lesson-notes-panel${isDarkMode ? " dark" : ""}`}
            style={panelStyleForState(state)}
          >
            <div className="lesson-notes-panel-header">
              <h3>Highlights & notes</h3>
              <button
                type="button"
                className="lesson-notes-close"
                aria-label="Close highlights and notes"
                onClick={onClose}
              >
                <i className="fa-solid fa-xmark" />
              </button>
            </div>
            <div className="lesson-notes-tabs" role="tablist">
              {TABS.map((entry) => (
                <button
                  key={entry.id}
                  type="button"
                  role="tab"
                  aria-selected={tab === entry.id}
                  className={`lesson-notes-tab${
                    tab === entry.id ? " active" : ""
                  }`}
                  onClick={() => setTab(entry.id)}
                >
                  {entry.label}
                </button>
              ))}
            </div>
            <div className="lesson-notes-list">
              {filteredNotes.length === 0 && (
                <p className="lesson-notes-empty">
                  {emptyCopy(tab, orderedNotes.length > 0)}
                </p>
              )}
              {groups.map((group) => (
                <section
                  key={group.sectionNumber}
                  className="lesson-notes-group"
                >
                  <h4 className="lesson-notes-group-title">
                    Lesson {group.sectionNumber}
                    {group.sectionTitle ? ` · ${group.sectionTitle}` : ""}
                  </h4>
                  {group.items.map((item) => (
                    <div
                      key={`${item.sectionNumber}::${item.animationSectionId}`}
                      className="lesson-notes-item"
                      onClick={() => onJump(item)}
                    >
                      <div className="lesson-notes-item-top">
                        <div className="lesson-notes-item-kinds">
                          {itemHasNote(item) ? (
                            <StickyNote size={13} strokeWidth={1.75} />
                          ) : (
                            <Highlighter size={13} strokeWidth={1.75} />
                          )}
                        </div>
                        <button
                          type="button"
                          className="lesson-notes-delete"
                          aria-label="Delete"
                          onClick={(event) => {
                            event.stopPropagation();
                            onDelete(item);
                          }}
                        >
                          <Trash2 size={13} strokeWidth={1.75} />
                        </button>
                      </div>
                      <p
                        className={`lesson-notes-item-preview${
                          item.missing ? " missing" : ""
                        }`}
                      >
                        {previewText(item)}
                      </p>
                      {itemHasNote(item) ? (
                        <p className="lesson-notes-item-note">{item.note}</p>
                      ) : null}
                    </div>
                  ))}
                </section>
              ))}
            </div>
          </aside>
        </>
      )}
    </Transition>
  );
}
